import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import "react-datepicker/dist/react-datepicker.css";
import { Col, Row, FormGroup, Button, FormControl } from "react-bootstrap";
import { FormControlLabel, Radio, RadioGroup, TextField } from "@mui/material";
import { MdOutlineModeEditOutline, MdSupervisedUserCircle, MdAccountCircle } from "react-icons/md";
import SpecialistSearch from "../../shared/SpecialistSearch";
import Lead from "../../shared/Lead";
import * as API from "../../api";
import Loading from "../../shared/Loading";
import Select from "../../shared/Select";
import SelectDuration from "./SelectDuration";
import SelectReferralInitiatedBy from "./SelectReferralInitiatedBy";
import SpecialistSearchFormFields from "./SpecialistSearchFormFields";
import SelectPcp from './SelectPcp';
import TextArea from "../../shared/TextArea";
import SelectAssignee from "./SelectAssignee";
import PriorityBadge from "../../shared/PriorityBadge";
import Patient from "../../shared/Patient";
import { Task, Settings } from "../../helpers/Contexts";

const GeneralPatientRequestForm = ({ state, task, dispatch, isCall, formHandler }) => {
  const settings = useContext(Settings);
  const [error, setError] = useState("");
  const [taskSubmitted, setTaskSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReferralRequestFields, setShowReferralRequestFields] = useState(task.general_patient_request_status == 'referral_request');
  const [specialistReferralRequestApproved, setSpecialistReferralRequestApproved] = useState(null);
  const [selectedSpecialistGroup, setSelectedSpecialistGroup] = useState(null);
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);
  const [specialistDisabled, setSpecialistDisabled] = useState(true);
  const [specialistGroupDisabled, setSpecialistGroupDisabled] = useState(true);
  const [selectedSpecialistPractice, setSelectedSpecialistPractice] = useState(null);
  const [searchedSpecialistPractices, setSearchedSpecialistPractices] = useState(null);
  const [specialistReferralRequestFormSubmitted, setSpecialistReferralRequestFormSubmitted] = useState(task.specialist_referral_request_approved);

  const history = useHistory();

  const TaskSchema = Yup.object().shape({
    priority: Yup.string().oneOf(["low", "medium", "high"]).nullable(),
    general_patient_request_status: Yup.string().required("Type is required."),
    preferred_contact_method: Yup.string()
      .required("Patient preferred contact method isrequired")
      .oneOf(["phone", "text", "email"]),
    notes: Yup.string().required("Notes is required"),
    assignee: Yup.string().required(
      "Assignee is required. Select a support group or agent to be assigned to this task."
    ),
  });

  return (
    <>
      <Formik
        initialValues={
          task.isNew || isCall
            ? {
                priority: "",
                general_patient_request_status: "",
                preferred_contact_method: "",
                assignee: "",
                assignee_id: "",
                assignee_support_group_id: "",
                specialist_group_id: "",
                specialist_id: "",
                specialist_type: "",
                specialist_practice_id: "",
                specialist_referral_request_approved: null,
                specialist_referral_request_denied_reason: null,
                ordering_provider_id: "",
                duration: "",
                referral_initiated_by: "",
                reason: null,
              }
            : {
                ordering_provider_id: task.ordering_provider_id,
                priority: task.priority,
                general_patient_request_status: task.general_patient_request_status,
                preferred_contact_method: task.preferred_contact_method,
                notes: task.notes,
                assignee: task.assignee.name,
                specialist_group_id: task.specialist_group_id,
                specialist_id: task.specialist_id,
                specialist_type: task.specialist_type,
                specialist_practice_id: task.specialist_practice_id,
                reason: task.reason,
                duration: task.duration,
                referral_initiated_by: task.referral_initiated_by,
                specialist_referral_request_approved: task.specialist_referral_request_approved,
                specialist_referral_request_denied_reason: task.specialist_referral_request_denied_reason
              }
        }
        validateOnChange={true}
        validationSchema={TaskSchema}
        onSubmit={(values) => {
          setLoading(true);
          if (isCall) {
            formHandler({
              task: {
                task_type: "general_patient_request",
                status: "open",
                priority: values.priority,
                patient_id: task.patient.id,
                general_patient_request_status: values.general_patient_request_status,
                notes: values.notes,
                preferred_contact_method: values.preferred_contact_method,
                assignee_id: values.assignee_id && values.assignee_id,
                assignee_support_group_id:
                  values.assignee_support_group_id && values.assignee_support_group_id,
                assigner_id: settings.me.id,
                assigner_support_group_id: settings.me.support_group_id,
                id: task.id,
                referral_initiated_by: values.referral_initiated_by,
                reason: values.reason,
                specialist_id: values.specialist_id,
                specialist_group_id: values.specialist_group_id,
                specialist_type: values.specialist_type,
                specialist_practice_id: values.specialist_practice_id,
                supporting_document: values.supporting_document,
                supporting_document_2: values.supporting_document_2,
                supporting_document_3: values.supporting_document_3,
              },
            });
          } else {
            if (task.isNew) {
              API.postTask({
                task: {
                  task_type: "general_patient_request",
                  status: "open",
                  priority: values.priority,
                  patient_id: task.patient.id,
                  general_patient_request_status: values.general_patient_request_status,
                  notes: values.notes,
                  preferred_contact_method: values.preferred_contact_method,
                  assignee_id: values.assignee_id && values.assignee_id,
                  assignee_support_group_id:
                    values.assignee_support_group_id && values.assignee_support_group_id,
                  assigner_id: settings.me.id,
                  assigner_support_group_id: settings.me.support_group_id,
                  specialist_id: values.specialist_id,
                  specialist_group_id: values.specialist_group_id,
                  specialist_type: values.specialist_type,
                  referral_initiated_by: values.referral_initiated_by,
                  specialist_practice_id: values.specialist_practice_id,
                  supporting_document: values.supporting_document,
                  supporting_document_2: values.supporting_document_2,
                  supporting_document_3: values.supporting_document_3,
                  duration: values.duration,
                  reason: values.reason,
                  ordering_provider_id: values.ordering_provider_id
                },
              })
                .then((res) => {
                  setLoading(false);
                  dispatch({ type: "set_loading", payload: true });
                  history.push(`/physicians/tasks/item/${res.task.id}`);
                  setTaskSubmitted(true);
                })
                .catch((err) => {
                  console.log(err.data);
                  setError(err);
                });
            } else {
              task.formHandler({
                task: {
                  id: task.id,
                  priority: values.priority,
                  general_patient_request_status: values.general_patient_request_status,
                  notes: values.notes,
                  preferred_contact_method: values.preferred_contact_method,
                  assigner_id: settings.me.id,
                  assigner_support_group_id: settings.me.support_group_id,

                  // SPECIALIST REFERRAL REQUEST FIELDS
                  specialist_id: values.specialist_id,
                  specialist_group_id: values.specialist_group_id,
                  specialist_type: values.specialist_type,
                  specialist_practice_id: values.specialist_practice_id,
                  duration: values.duration,
                  supporting_document: values.supporting_document,
                  supporting_document_2: values.supporting_document_2,
                  supporting_document_3: values.supporting_document_3,
                  referral_initiated_by: values.referral_initiated_by,
                  reason: values.reason,
                  ordering_provider_id: values.ordering_provider_id
                },
              });
            }
          }
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => {
          function renderForm() {
            return (
              <>
                <Select label="Priority" name="priority" value={values.priority}>
                  <option value="">{"Choose a priority level"}</option>
                  <option value={"low"}>Low</option>
                  <option value={"medium"}>Medium</option>
                  <option value={"high"}>High</option>
                </Select>

                <Select
                  label="Request Type/Topic"
                  value={values.general_patient_request_status}
                  name="general_patient_request_status"
                  onChange={(el) => {
                    if (el.target.value == 'referral_request') {
                      if (task.patient.specialist_referrals_enabled == true) {
                        setShowReferralRequestFields(true);
                      } else {
                        alert("This patient is not in a health plan that allows for us to send specialist referrals. Please select a patient in a health plan we have specialist referrals enabled for.");
                        return;
                      }
                    } else {
                      setShowReferralRequestFields(false);
                    }
                    setFieldValue('general_patient_request_status', el.target.value);
                  }}
                >
                  <option value={""}>{"Select the type of request"}</option>
                  {settings.general_patient_request_statuses.map((option, i) => (
                    <option className={"type"} key={i} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>

                { showReferralRequestFields == true && (
                  <>
                    <SpecialistSearchFormFields
                      state={state}
                      task={task}
                      values={values}
                      selectedSpecialistGroup={selectedSpecialistGroup}
                      setSelectedSpecialistPractice={setSelectedSpecialistPractice}
                      setSelectedSpecialist={setSelectedSpecialist}
                      setSelectedSpecialistGroup={setSelectedSpecialistGroup}
                      setFieldValue={setFieldValue}
                      setSpecialistGroupDisabled={setSpecialistGroupDisabled}
                      setSpecialistDisabled={setSpecialistDisabled}
                     />
                    <SelectDuration required={false} value={values.duration} />
                    <SelectReferralInitiatedBy value={values.referral_initiated_by} />
                    <label>{"Ordering Provider"}</label>
                    <SelectPcp
                      valueLabel={ task.patient.pcp }
                      value={ task.ordering_provider_id }
                      name="ordering_provider_id"
                      pcpRequired={true}
                      active={false}
                      required={true}
                      setFieldValue={setFieldValue}
                    />

                  </>)
                }

                <TextArea
                  label="Notes"
                  value={values.notes}
                  name="notes"
                  placeholder="Please write additional notes"
                />
                <Select
                  label="Patient Preferred Contact Method"
                  name="preferred_contact_method"
                  value={values.preferred_contact_method}
                >
                  <option value={""}>{"Select patient preferred contact method"}</option>
                  <option value="text">{"Text"}</option>
                  <option value="phone">{"Phone"}</option>
                </Select>
              </>
            );
          }

          function renderFormVals(showSpecialistReferralRequestFields) {
            return (
              <div>
                <label>
                  Priority
                  {values.priority && <PriorityBadge priority={values.priority} />}
                </label>
                { values.general_patient_request_status == 'referral_request' && (<>
                  <label>Specialist Requested</label>
                  {task.specialist || "None"}

                  <label>Specialist Group Requested</label>
                  {task.specialist_group || "None"}
                  <br />
                  <label>Specialist Type Requested</label>
                  {task.specialist_type || "None"}
                  <label>Ordering Provider</label>
                  {task.ordering_provider || "None"}
                  <label>Referral Initiated by</label>
                  {task.referral_initiated_by || "None"}
                  <label>Duration</label>
                  {task.duration || "None"}
                  <br />

                  { specialistReferralRequestFormSubmitted != null && (
                    <>
                      <label>Approval Status</label>
                      This specialist referral request has been <b>{ task.specialist_referral_request_approved == true ? "Approved" : "Denied" }.</b>
                      {task.specialist_referral_request_denied_reason && (
                        <>
                          <label>Denial Reason</label>
                          {task.specialist_referral_request_denied_reason || "None"}
                        </>)
                      }
                    </>)}

                  { specialistReferralRequestFormSubmitted == null && showSpecialistReferralRequestFields == true && (
                      <>
                        <label className='m-t-lg'> Approve Specialist Referral Request? </label>
                        <div>
                          Click "Approve" or "Deny", write a reason, and click "Save".
                        </div>
                        <RadioGroup onChange={(el) => {
                          setSpecialistReferralRequestApproved(el.target.value == 'specialist_referral_request_approved')
                        }}>
                          <FormControlLabel
                            value={'specialist_referral_request_approved'}
                            label={<>
                              <i className={'fa fa-check'}></i> Approve
                             </>}
                            control={ <Radio /> }
                          />
                          <FormControlLabel
                            value={'specialist_referral_request_denied'}
                            label={<>
                              <i className={'fa fa-times'}></i> Deny
                             </>}
                            control={ <Radio /> }
                          />
                        </RadioGroup>
                        { specialistReferralRequestApproved == false && (<>
                          <TextArea
                            label="Reason Why You are Denying"
                            value={values.specialist_referral_request_denied_reason}
                            name="specialist_referral_request_denied_reason"
                            placeholder="Please write your reason for denying."
                          />
                        </>)}
                        { specialistReferralRequestApproved != false && (<>
                          <TextArea
                            label="Reason"
                            value={values.reason}
                            name="reason"
                            required={true}
                            placeholder="Please write reason for referral."
                          />
                        </>) }
                        <label>Support Documents</label>
                        <span id="from">Attach supporting documents, if available.</span>
                        <FormGroup controlId="formFile" className="mb-3">
                          <FormControl
                            type="file"
                            onChange={(e) => {
                              setFieldValue("supporting_document", e.target.files[0])
                            }}
                          />
                        </FormGroup>
                        <FormGroup controlId="formFile" className="mb-3">
                          <FormControl
                            type="file"
                            onChange={(e) => {
                              setFieldValue("supporting_document_2", e.target.files[0])
                            }}
                          />
                        </FormGroup>
                        <FormGroup controlId="formFile" className="mb-3">
                          <FormControl
                            type="file"
                            onChange={(e) => {
                              setFieldValue("supporting_document_3", e.target.files[0])
                            }}
                          />
                        </FormGroup>
                        <button className='btn btn-default width-auto m-t-lg' disabled={loading} onClick={(el) => {
                            if (specialistReferralRequestApproved == true) {
                              if ([task.ordering_provider, task.duration, task.specialist_group, values.reason].includes(null)) {
                                let messageMissingFields = "The following fields are required to approve and submit a referral: \n \n";
                                let arr = [];
                                if (task.ordering_provider == null) {
                                  arr.push("Ordering Provider");
                                }
                                if (task.specialist_group == null ) {
                                  arr.push("Specialist Group");
                                }
                                if (values.reason == null) {
                                  arr.push("Reason");
                                }

                                if (values.duration == null) {
                                  arr.push("Duration");
                                }


                                messageMissingFields += arr.join(', ');
                                messageMissingFields += "\n \nPlease click the blue 'Edit Task' button on the left side of the screen, complete the required fields, and resubmit the task. \n"
                                messageMissingFields += "\nIMPORTANT: After completing the required fields, return to this form, click Approve, and then press the Save button to submit the referral."
                                alert(messageMissingFields);
                                return;
                              }
                            } else {
                              if (values.specialist_referral_request_denied_reason == null) {
                                alert("You must select Approve or Deny and enter a reason before saving.");
                                return;
                              }
                            }

                            setLoading(true);

                            API.updateTask(
                              task.id,
                              {
                                task: {
                                  id: task.id,
                                  reason: values.reason,
                                  supporting_document: values.supporting_document,
                                  supporting_document_3: values.supporting_document_3,
                                  supporting_document_2: values.supporting_document_2,
                                  specialist_referral_request_denied_reason: values.specialist_referral_request_denied_reason,
                                  specialist_referral_request_approved: specialistReferralRequestApproved
                                }
                              }
                            ).then((el) => {
                              setSpecialistReferralRequestApproved(specialistReferralRequestApproved);
                              setLoading(false);
                              window.location.reload();
                            }).catch((err) => {
                              setLoading(false);
                            });
                          }}
                        >
                          <i className='fa fa-check'></i> { loading != true ? "Save" : "Saving..." }
                        </button>
                        </>)}
                      </>
                  )}
                <label>Notes</label>
                  {values.notes}
                <label>Patient Preferred Contact Method</label>
                {values.preferred_contact_method &&
                  values.preferred_contact_method.charAt(0).toUpperCase() +
                    values.preferred_contact_method.slice(1)}
              </div>
            );
          }

          function renderNewTaskForm() {
            return (
              <Form>
                <Row className="flex">
                  <Col md={5} className="pad20 form">
                    <Lead message={"New " + task.type} />
                    <br />
                    <SelectAssignee name="assignee" setFieldValue={setFieldValue} />
                    {renderForm()}
                    <button id="btn outline" type="submit" disabled={isSubmitting}>
                      Submit General Patient Request
                    </button>
                  </Col>
                  <Col md={7} className="pad20 border-container">
                    {loading && !taskSubmitted && <Loading message={"Creating Task "} />}
                    {error && <Lead message={"Error processing request"} />}

                    <Patient patient={{ ...task.patient }} />
                    <hr />

                    <label className="agent">
                      {"Assignee "}{" "}
                      <span id="from">
                        {values.assignee_id && values.assignee_support_group_id && (
                          <>
                            <MdAccountCircle className="task-agent" />
                            <b> {values.assignee}</b>
                          </>
                        )}

                        {!values.assignee_id && values.assignee_support_group_id && (
                          <>
                            {" "}
                            <MdSupervisedUserCircle className="task-agent" />
                            <b>{values.assignee} </b>
                          </>
                        )}
                      </span>
                    </label>
                    <hr />
                  </Col>
                </Row>
              </Form>
            );
          }

          function renderEditTaskForm() {
            const { taskValues, setTaskValues } = useContext(Task);

            return (
              <>
                {task.actions}
                <Col md={9} className="no-gutters task-cols">
                  {taskValues.editing.requested && (
                    <Col md={6} className="pad20">
                      <Form>
                        <MdOutlineModeEditOutline id="pencil-icon" />
                        <Lead message={"Editing General Patient Request"} />
                        <br />
                        <br />

                        {renderForm()}
                        <button id="btn outline" type="submit">
                          Submit General Patient Request
                        </button>
                      </Form>
                    </Col>
                  )}
                  {
                    !taskValues.editing.requested && (
                      <Col md={taskValues.editing.requested ? 6 : null} className={"pad20"}>
                        <Lead message={task.label} />
                        <br />
                        {new Date(task.created_at).toLocaleString([], {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {renderFormVals(true)}
                      </Col>
                    )
                  }
                </Col>
              </>
            );
          }

          return (
            <>
              <Row className="flex">
                {(task.isNew || isCall) && renderNewTaskForm()}
                {!task.isNew && !isCall && renderEditTaskForm()}
              </Row>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default GeneralPatientRequestForm;
